// Activity — the flat, filterable chronological feed of deployment events.
// Filters live in the URL (shareable) and are always visible, on every visit —
// no mode-dependent hiding like the legacy list view.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BookmarkAdd as SaveIcon,
  Bolt as ActivityIcon,
  Close as CloseIcon,
  FilterListOff as ClearIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { subHours } from 'date-fns/subHours';
import { subDays } from 'date-fns/subDays';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { TimePreset, useV2Filters } from '../hooks/useV2Filters';
import { getColors, rounded } from '../theme/tokens';
import PageHeader from '../components/PageHeader';
import FilterSearch from '../components/FilterSearch';
import DeploymentRow, { DEFAULT_COLUMN_WIDTHS, DeploymentRowHeader } from '../components/DeploymentRow';
import EmptyState from '../components/EmptyState';
import { ColumnConstraints, useColumnWidths } from '../hooks/useColumnWidths';
import InfoPopover from '../components/InfoPopover';
import { serviceFilterKey } from '../lib/records';

// Same shape + storage key as the legacy dashboard so presets carry over.
interface SavedFilter {
  id: string;
  name: string;
  filters: {
    selectedEnvs: string[];
    selectedTypes: string[];
    selectedApps: string[];
    selectedServices: string[];
  };
}

const SAVED_FILTERS_KEY = 'cd-dashboard-saved-filters';
const COLUMN_WIDTHS_KEY = 'cd-dashboard-v2-activity-column-widths';

const COLUMN_CONSTRAINTS: ColumnConstraints = {
  service: { min: 120, max: 480 },
  version: { min: 60, max: 240 },
  time: { min: 90, max: 220 },
};

const loadSavedFilters = (): SavedFilter[] => {
  try {
    const raw = localStorage.getItem(SAVED_FILTERS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const TIME_OPTIONS: Array<{ value: TimePreset; label: string }> = [
  { value: 'all', label: 'All time' },
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom range' },
];

const ActivityPage: React.FC = () => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const {
    staticRecords,
    loading,
    loadingProgress,
    uniqueApps,
    uniqueServices,
    uniqueEnvs,
    uniqueTypes,
    uniqueBuildUsers,
  } = useDeploymentData();
  const filters = useV2Filters();

  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>(loadSavedFilters);
  const [editingFilterId, setEditingFilterId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const { widths, setWidth, resetWidth } = useColumnWidths(
    COLUMN_WIDTHS_KEY,
    DEFAULT_COLUMN_WIDTHS,
    COLUMN_CONSTRAINTS
  );

  const persistSavedFilters = (next: SavedFilter[]) => {
    setSavedFilters(next);
    try {
      localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(next));
    } catch {
      // storage unavailable — keep in-memory only
    }
  };

  const filtered = useMemo(() => {
    let out = [...staticRecords];

    // Deployment ID is a precise lookup — short-circuits the other filters,
    // matching legacy behavior so a pasted ID is always findable.
    if (filters.depId.trim()) {
      const q = filters.depId.toLowerCase().trim();
      return out.filter(
        r =>
          (r.id && r.id.toLowerCase().includes(q)) ||
          (r.deployment_id && r.deployment_id.toLowerCase().includes(q))
      );
    }

    if (filters.time === 'custom') {
      // Custom bounds from the URL; an unset bound is open-ended.
      const start = filters.customFrom ? new Date(filters.customFrom) : null;
      const end = filters.customTo ? new Date(filters.customTo) : null;
      if (start || end) {
        out = out.filter(r => {
          const t = new Date(r.timestamp);
          if (start && t < start) return false;
          if (end && t > end) return false;
          return true;
        });
      }
    } else if (filters.time !== 'all') {
      const now = new Date();
      const start =
        filters.time === '1h'
          ? subHours(now, 1)
          : filters.time === '24h'
          ? subHours(now, 24)
          : filters.time === '7d'
          ? subDays(now, 7)
          : subDays(now, 30);
      out = out.filter(r => {
        const t = new Date(r.timestamp);
        return t >= start && t <= now;
      });
    }

    const inSet = (set: string[], v?: string) => set.length === 0 || (v ? set.includes(v) : false);
    out = out.filter(
      r =>
        inSet(filters.apps, r.app) &&
        inSet(filters.services, r.serviceName ? serviceFilterKey(r.type, r.serviceName) : undefined) &&
        inSet(filters.envs, r.envName) &&
        inSet(filters.types, r.type) &&
        inSet(filters.users, r.buildUser)
    );

    return out;
  }, [staticRecords, filters.depId, filters.time, filters.customFrom, filters.customTo, filters.apps, filters.services, filters.envs, filters.types, filters.users]);

  const sorted = useMemo(
    () =>
      [...filtered].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      ),
    [filtered]
  );

  // Reset page whenever the filter result changes shape.
  useEffect(() => {
    setPage(0);
  }, [filters.apps, filters.services, filters.envs, filters.types, filters.users, filters.depId, filters.time, filters.customFrom, filters.customTo]);

  const paginated = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage]
  );

  const canSave =
    filters.apps.length > 0 ||
    filters.services.length > 0 ||
    filters.envs.length > 0 ||
    filters.types.length > 0;

  const handleSaveFilter = () => {
    const next: SavedFilter = {
      id: `filter-${Date.now()}`,
      name: 'Saved filter',
      filters: {
        selectedEnvs: filters.envs,
        selectedTypes: filters.types,
        selectedApps: filters.apps,
        selectedServices: filters.services,
      },
    };
    persistSavedFilters([...savedFilters, next]);
    setEditingFilterId(next.id);
  };

  const applySavedFilter = (f: SavedFilter) => {
    filters.applyFilterSet({
      apps: f.filters.selectedApps,
      services: f.filters.selectedServices,
      envs: f.filters.selectedEnvs,
      types: f.filters.selectedTypes,
    });
  };

  return (
    <Box>
      <PageHeader
        title="Timeline"
        subtitle="Every deployment event, newest first"
      />

      {/* Filter bar */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2, borderRadius: `${rounded.lg}px`, borderColor: c.hairline }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ flexGrow: 1, minWidth: 280 }}>
            <FilterSearch
              apps={uniqueApps}
              services={uniqueServices}
              envs={uniqueEnvs}
              types={uniqueTypes}
              users={uniqueBuildUsers}
              selectedApps={filters.apps}
              selectedServices={filters.services}
              selectedEnvs={filters.envs}
              selectedTypes={filters.types}
              selectedUsers={filters.users}
              depId={filters.depId}
              setApps={filters.setApps}
              setServices={filters.setServices}
              setEnvs={filters.setEnvs}
              setTypes={filters.setTypes}
              setUsers={filters.setUsers}
              setDepId={filters.setDepId}
            />
          </Box>
          <Select
            size="small"
            value={filters.time}
            onChange={e => filters.setTime(e.target.value as TimePreset)}
            sx={{ fontSize: '0.85rem', minWidth: 140 }}
          >
            {TIME_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.85rem' }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {filters.time === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                ampm
                label="Start"
                value={filters.customFrom ? dayjs(filters.customFrom) : null}
                onChange={v => filters.setCustomFrom(v?.isValid() ? v.toISOString() : null)}
                slotProps={{ textField: { size: 'small', sx: { width: 230 } } }}
              />
              <DateTimePicker
                ampm
                label="End"
                value={filters.customTo ? dayjs(filters.customTo) : null}
                onChange={v => filters.setCustomTo(v?.isValid() ? v.toISOString() : null)}
                slotProps={{ textField: { size: 'small', sx: { width: 230 } } }}
              />
            </LocalizationProvider>
          )}
          <Tooltip title="Save current filters" arrow>
            <span>
              <IconButton size="small" onClick={handleSaveFilter} disabled={!canSave}>
                <SaveIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Clear all filters" arrow>
            <span>
              <IconButton size="small" onClick={filters.clearAll} disabled={!filters.hasActiveFilters}>
                <ClearIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Saved filters */}
        {savedFilters.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: c.inkMuted, fontWeight: 600, mr: 0.5 }}>
              Saved
              <InfoPopover entry="savedFilters" size={13} />
            </Typography>
            {savedFilters.map(f =>
              editingFilterId === f.id ? (
                <TextField
                  key={f.id}
                  size="small"
                  defaultValue={f.name}
                  autoFocus
                  onBlur={e => {
                    const name = e.target.value.trim();
                    if (name) {
                      persistSavedFilters(
                        savedFilters.map(sf => (sf.id === f.id ? { ...sf, name } : sf))
                      );
                    }
                    setEditingFilterId(null);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') setEditingFilterId(null);
                  }}
                  sx={{ width: 160, '& .MuiInputBase-input': { py: 0.4, px: 1, fontSize: '0.8rem' } }}
                />
              ) : (
                <Chip
                  key={f.id}
                  label={f.name}
                  size="small"
                  onClick={() => applySavedFilter(f)}
                  onDoubleClick={() => setEditingFilterId(f.id)}
                  onDelete={() => persistSavedFilters(savedFilters.filter(sf => sf.id !== f.id))}
                  deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.74rem',
                    backgroundColor: `${c.primary}10`,
                    color: c.primary,
                    border: `1px solid ${c.primary}33`,
                  }}
                />
              )
            )}
          </Box>
        )}
      </Paper>

      {/* Feed */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: `${rounded.lg}px`, borderColor: c.hairline, overflow: 'hidden' }}
      >
        {loading && paginated.length === 0 ? (
          <Box sx={{ p: 2 }}>
            {loadingProgress && (
              <Typography variant="body2" sx={{ opacity: 0.6, mb: 1.5 }}>
                Loading chunk {loadingProgress.current} of {loadingProgress.total}…
              </Typography>
            )}
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={42} sx={{ borderRadius: 1, mb: 1 }} />
            ))}
          </Box>
        ) : paginated.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <EmptyState
              icon={<ActivityIcon />}
              title={filters.hasActiveFilters ? 'No deployments match your filters' : 'No deployments found'}
              body={
                filters.hasActiveFilters
                  ? 'Try clearing filters or widening the time range.'
                  : 'Deployments will appear here once services report in.'
              }
              action={
                filters.hasActiveFilters ? (
                  <Button variant="outlined" size="small" startIcon={<ClearIcon />} onClick={filters.clearAll}>
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          </Box>
        ) : (
          <>
            <DeploymentRowHeader widths={widths} onResize={setWidth} onReset={resetWidth} />
            {paginated.map((r, i) => (
              <DeploymentRow key={r.id} record={r} widths={widths} index={i} />
            ))}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={sorted.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={e => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{ borderTop: `1px solid ${c.hairline}`, backgroundColor: c.canvasSoft }}
            />
          </>
        )}
      </Paper>

      <Typography variant="caption" sx={{ opacity: 0.5, display: 'block', mt: 1 }}>
        {sorted.length} of {staticRecords.length} current records
      </Typography>
    </Box>
  );
};

export default ActivityPage;
